import React, { useEffect, useState, useCallback } from 'react';
import {
  FaInstagram, FaYoutube, FaGithub, FaTwitter, FaTwitch,
  FaDiscord, FaTiktok, FaLinkedin, FaGlobe, FaTimes, FaPlus, FaSave, FaTrash
} from 'react-icons/fa';
import { api } from '../../utils/api';
import { useAuth } from '../../context/AuthContext';

const ICON_OPTIONS = [
  { key: 'instagram', Icon: FaInstagram },
  { key: 'youtube',   Icon: FaYoutube },
  { key: 'github',    Icon: FaGithub },
  { key: 'twitter',   Icon: FaTwitter },
  { key: 'twitch',    Icon: FaTwitch },
  { key: 'discord',   Icon: FaDiscord },
  { key: 'tiktok',    Icon: FaTiktok },
  { key: 'linkedin',  Icon: FaLinkedin },
  { key: 'globe',     Icon: FaGlobe },
];

const blankNew = { name: '', url: '', icon: 'globe', order: 0 };

function isValidHttpUrl(u) {
  try {
    const url = new URL(u);
    return url.protocol === 'https:' || url.protocol === 'http:';
  } catch { return false; }
}

export default function AdminPanel({ onClose }) {
  const { token, user } = useAuth();
  const [links, setLinks] = useState([]);
  const [drafts, setDrafts] = useState({});
  const [newLink, setNewLink] = useState(blankNew);
  const [busyId, setBusyId] = useState(null);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');

  const load = useCallback(async () => {
    try {
      const data = await api('/api/social');
      setLinks(data);
      const d = {};
      data.forEach(l => { d[l._id] = { name: l.name, url: l.url, icon: l.icon, order: l.order, active: l.active !== false }; });
      setDrafts(d);
    } catch (e) {
      setError('Failed to load links');
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (!user || user.role !== 'admin') return null;

  const notifyUpdate = () => window.dispatchEvent(new Event('social-links-updated'));

  const setDraft = (id, patch) => {
    setDrafts(prev => ({ ...prev, [id]: { ...prev[id], ...patch } }));
  };

  const flash = (msg) => {
    setInfo(msg);
    setTimeout(() => setInfo(''), 1800);
  };

  const save = async (id) => {
    setError('');
    const d = drafts[id];
    if (!d?.name?.trim() || !d?.url?.trim() || !d?.icon?.trim()) {
      setError('All fields are required');
      return;
    }
    if (!isValidHttpUrl(d.url)) {
      setError('URL must start with http:// or https://');
      return;
    }
    setBusyId(id);
    try {
      await api(`/api/social/${id}`, {
        method: 'PUT',
        body: {
          name: d.name.trim(),
          url: d.url.trim(),
          icon: d.icon.trim().toLowerCase(),
          order: Number(d.order) || 0,
          active: !!d.active,
        },
        token,
      });
      await load();
      notifyUpdate();
      flash('Saved');
    } catch (e) {
      setError(e.message || 'Failed to save');
    } finally {
      setBusyId(null);
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this social link?')) return;
    setBusyId(id);
    setError('');
    try {
      await api(`/api/social/${id}`, { method: 'DELETE', token });
      await load();
      notifyUpdate();
      flash('Deleted');
    } catch (e) {
      setError(e.message || 'Failed to delete');
    } finally {
      setBusyId(null);
    }
  };

  const create = async (e) => {
    e.preventDefault();
    setError('');
    if (!newLink.name.trim() || !newLink.url.trim() || !newLink.icon.trim()) {
      setError('All fields are required');
      return;
    }
    if (!isValidHttpUrl(newLink.url)) {
      setError('URL must start with http:// or https://');
      return;
    }
    setBusyId('new');
    try {
      await api('/api/social', {
        method: 'POST',
        body: {
          name: newLink.name.trim(),
          url: newLink.url.trim(),
          icon: newLink.icon.trim().toLowerCase(),
          order: Number(newLink.order) || 0,
        },
        token,
      });
      setNewLink(blankNew);
      await load();
      notifyUpdate();
      flash('Added');
    } catch (e) {
      setError(e.message || 'Failed to create');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="modal-overlay" onMouseDown={(e) => {
      if (e.target === e.currentTarget) onClose();
    }}>
      <div className="admin-panel">
        <header className="admin-panel-head">
          <h2>Admin Panel — Social Links</h2>
          <button className="icon-btn" onClick={onClose} aria-label="Close"><FaTimes /></button>
        </header>

        {error && <div className="error">{error}</div>}
        {info && <div className="info">{info}</div>}

        <section className="admin-section">
          <h3>Existing Links</h3>
          {links.length === 0 && <p className="muted">No social links yet.</p>}
          {links.map((l) => {
            const d = drafts[l._id] || {};
            const IconComp = (ICON_OPTIONS.find(o => o.key === (d.icon || '').toLowerCase()) || ICON_OPTIONS[8]).Icon;
            return (
              <div className="admin-link-row" key={l._id}>
                <div className="admin-link-preview"><IconComp /></div>
                <div className="admin-link-fields">
                  <div className="field-grid">
                    <label>
                      Name
                      <input
                        value={d.name || ''}
                        onChange={(e) => setDraft(l._id, { name: e.target.value })}
                        maxLength={50}
                      />
                    </label>
                    <label>
                      Icon
                      <select
                        value={d.icon || 'globe'}
                        onChange={(e) => setDraft(l._id, { icon: e.target.value })}
                      >
                        {ICON_OPTIONS.map(o => (
                          <option key={o.key} value={o.key}>{o.key}</option>
                        ))}
                      </select>
                    </label>
                    <label>
                      Order
                      <input
                        type="number"
                        value={d.order ?? 0}
                        onChange={(e) => setDraft(l._id, { order: e.target.value })}
                      />
                    </label>
                    <label className="active-toggle">
                      <input
                        type="checkbox"
                        checked={!!d.active}
                        onChange={(e) => setDraft(l._id, { active: e.target.checked })}
                      />
                      <span>Active</span>
                    </label>
                  </div>
                  <label className="full">
                    URL
                    <input
                      value={d.url || ''}
                      onChange={(e) => setDraft(l._id, { url: e.target.value })}
                      placeholder="https://..."
                      maxLength={500}
                    />
                  </label>
                </div>
                <div className="admin-link-actions">
                  <button
                    className="btn btn-sm btn-primary"
                    onClick={() => save(l._id)}
                    disabled={busyId === l._id}
                    title="Save"
                  >
                    <FaSave /> Save
                  </button>
                  <button
                    className="btn btn-sm btn-danger"
                    onClick={() => remove(l._id)}
                    disabled={busyId === l._id}
                    title="Delete"
                  >
                    <FaTrash /> Delete
                  </button>
                </div>
              </div>
            );
          })}
        </section>

        <section className="admin-section">
          <h3>Add New Link</h3>
          <form className="admin-link-row admin-new" onSubmit={create}>
            <div className="admin-link-preview">
              {(() => {
                const IconComp = (ICON_OPTIONS.find(o => o.key === newLink.icon) || ICON_OPTIONS[8]).Icon;
                return <IconComp />;
              })()}
            </div>
            <div className="admin-link-fields">
              <div className="field-grid">
                <label>
                  Name
                  <input
                    value={newLink.name}
                    onChange={(e) => setNewLink({ ...newLink, name: e.target.value })}
                    maxLength={50}
                    placeholder="e.g. Twitch"
                  />
                </label>
                <label>
                  Icon
                  <select
                    value={newLink.icon}
                    onChange={(e) => setNewLink({ ...newLink, icon: e.target.value })}
                  >
                    {ICON_OPTIONS.map(o => (
                      <option key={o.key} value={o.key}>{o.key}</option>
                    ))}
                  </select>
                </label>
                <label>
                  Order
                  <input
                    type="number"
                    value={newLink.order}
                    onChange={(e) => setNewLink({ ...newLink, order: e.target.value })}
                  />
                </label>
              </div>
              <label className="full">
                URL
                <input
                  value={newLink.url}
                  onChange={(e) => setNewLink({ ...newLink, url: e.target.value })}
                  placeholder="https://..."
                  maxLength={500}
                />
              </label>
            </div>
            <div className="admin-link-actions">
              <button
                className="btn btn-sm btn-primary"
                type="submit"
                disabled={busyId === 'new'}
              >
                <FaPlus /> Add
              </button>
            </div>
          </form>
        </section>
      </div>
    </div>
  );
}
